import { useInfiniteQuery } from '@tanstack/react-query';
import { listTasksV2 } from 'services/TaskServices';
import { queryKeys } from '../../../config/queryClient';
import { LAYOUT } from '../../../utils/constants';

/**
 * Infinite query hook for a single column - SINGLE CACHE ARCHITECTURE
 *
 * This hook manages ALL pages for a column in one infinite query cache.
 * Page 1 is pre-populated by useInitialBoardLoad, subsequent pages are fetched on scroll.
 *
 * Benefits:
 * - Single cache per column (no dual-cache complexity)
 * - All data in one place
 * - Simpler mutations (only update this cache)
 * - Built-in pagination state management
 *
 * @param {string} columnId - The column/stage ID
 * @param {Object} filters - Filter parameters (searchKeyword, practiceArea, assignedTo, sortType, dueDateFilter, priority)
 * @returns {Object} - Query result with data, fetchNextPage, hasNextPage, etc.
 */
export const useColumnInfiniteQuery = (columnId, filters = {}) => {
  return useInfiniteQuery({
    queryKey: [...queryKeys.tasks.column(columnId), filters],

    queryFn: async ({ pageParam = 2 }) => {
      console.log('filters', filters);
      // Fetch page 2+ (page 1 pre-loaded by useInitialBoardLoad)
      const limit = pageParam === 1 ? LAYOUT.DEFAULT_PAGE_LIMIT : LAYOUT.LOAD_MORE_PAGE_LIMIT;

      // Build request payload per Task List v2 API documentation
      const requestData = {
        task_stage_id: columnId,
        page: pageParam,
      };

      // Add filters to body (per API documentation)
      if (filters.practiceArea) {
        requestData.practice_area = filters.practiceArea;
      }
      if (filters.assignedTo && filters.assignedTo.length > 0) {
        // API expects firm_user_ids array instead of assigned_to
        requestData.firm_user_ids = filters.assignedTo;
      }
      if (filters.sortType) {
        requestData.sort_type = filters.sortType;
      }
      if (filters.priority) {
        requestData.priority = filters.priority;
      }
      if (filters.dateFilter) {
        requestData.due_date_filter = filters.dateFilter;
      }

      // Note: keyword is passed as a separate parameter to match API signature
      const response = await listTasksV2({
        limit,
        data: requestData,
        keyword: filters.searchKeyword || false,
        excludeFromLoading: true,
        case_id: filters.case_id || undefined,
      });

      return {
        items: response?.data?.items || [],
        page: response?.data?.page || pageParam,
        hasMore: response?.data?.has_more || false,
        totalCount: response?.data?.total_count || 0,
      };
    },

    // Get the next page parameter
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.page + 1;
    },

    // Get the previous page parameter (optional, for bi-directional scroll)
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page <= 1) return undefined;
      return firstPage.page - 1;
    },

    initialData: undefined,

    enabled: false,

    notifyOnChangeProps: 'all',
  });
};
